import React, { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { DocumentReferenceAttachment } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/Attachment';
import { DocumentReferenceContent } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/modals/DocumentReferenceModal';
import FocusedSurvey from '@lifeomic/phc-web-toolkit/src/components/Surveys/Components/SubjectResponseViewer/FocusedSurvey';
import MarkdownViewer from '@lifeomic/phc-web-toolkit/src/components/MarkdownViewer';
import { memberDetailMessages } from './messages';
import {
  ExtractedResourceReviewCard,
  formatReviewDate,
} from './ExtractedResourceReviewCard';
import type { DocumentExtractionReviewSurface } from './documentExtractionV2Analytics';
import {
  getCommunicationSourceContent,
  getCommunicationSourceDate,
  getCommunicationSourceTitle,
} from './extractedCommunicationSource';
import {
  type ReviewableResourceType,
  type UnreviewedExtractedResource,
} from './useUnreviewedExtractedResources';
import { useSourceDocumentSiblings } from './useSourceDocumentSiblings';
import { useExtractedResourceSource } from './useExtractedResourceSource';
import { PulseLoader } from '../../../common/PulseLoader';

const useStyles = makeStyles(theme => ({
  sideBySide: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    height: '100%',
    minHeight: 0,
    width: '100%',
  },
  sourcePane: {
    flex: '1 1 50%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(4),
    overflow: 'hidden',
  },
  sourcePaneHeader: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
  },
  sourcePaneBody: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: theme.palette.background.default,
  },
  sourcePanePlaceholder: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: 0,
    padding: theme.spacing(2),
  },
  notePaneBody: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  noteHeaderDate: {
    color: theme.palette.text.hint,
    marginLeft: 'auto',
  },
  extractionPane: {
    flex: '1 1 50%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    overflowY: 'auto',
    paddingRight: theme.spacing(0.5),
  },
  queueHeader: {
    color: theme.palette.text.hint,
  },
}));

type ExtractedResourceReviewModalProps = {
  items: UnreviewedExtractedResource[];
  selected: UnreviewedExtractedResource;
  subjectId: string;
  typeLabelFor: (resourceType: ReviewableResourceType) => string;
  /**
   * When set, emit document-extraction review analytics at the persistence
   * seam. Omit on Pulse (Pulse uses `pulse_resource_reviewed` instead).
   */
  reviewAnalyticsSurface?: DocumentExtractionReviewSurface;
  onResolveItem: (item: {
    resourceType: ReviewableResourceType;
    id: string;
    resolution: 'approved' | 'rejected';
  }) => void;
  onDismiss: () => void;
};

/**
 * Review modal for extracted clinical resources surfaced on the Zori Pulse
 * page. Mirrors the document-extraction review experience: the source document
 * is shown side-by-side with the resource details and approve/reject controls.
 *
 * When several extracted resources share the same source document, they are all
 * shown as a review queue and the modal stays open until every one has been
 * approved or rejected.
 */
export const ExtractedResourceReviewModal: React.FC<
  ExtractedResourceReviewModalProps
> = ({
  items,
  selected,
  subjectId,
  typeLabelFor,
  reviewAnalyticsSurface,
  onResolveItem,
  onDismiss,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const { siblings } = useSourceDocumentSiblings(items, selected);
  const { source, isLoading: isSourceLoading } =
    useExtractedResourceSource(selected);

  // Keep the modal open until every resource sharing the source document has
  // been reviewed; only auto-close once the queue empties after having content.
  const hadSiblings = useRef(false);
  useEffect(() => {
    if (siblings.length > 0) {
      hadSiblings.current = true;
    } else if (hadSiblings.current) {
      onDismiss();
    }
  }, [siblings.length, onDismiss]);

  const sourceDocument = source?.document ?? null;
  const sourceQuestionnaireResponse = source?.questionnaireResponse ?? null;
  const sourceCommunication = source?.communication ?? null;
  const hasViewableAttachment = !!sourceDocument?.content?.[0]?.attachment?.url;
  const sourceAttachment = useMemo(
    () =>
      hasViewableAttachment && sourceDocument
        ? new DocumentReferenceAttachment(sourceDocument)
        : null,
    [hasViewableAttachment, sourceDocument],
  );

  const noteTitle = getCommunicationSourceTitle(sourceCommunication);
  const noteDate = getCommunicationSourceDate(sourceCommunication);
  const noteContent = getCommunicationSourceContent(sourceCommunication);

  // Whether a dedicated source pane is rendered for this source (document
  // attachment, survey response, or note). Used to suppress the provenance
  // source link that would otherwise duplicate it.
  const hasRenderedSource =
    !!sourceAttachment ||
    !!sourceQuestionnaireResponse ||
    !!sourceCommunication;

  const sourceTitle = sourceCommunication
    ? noteTitle ??
      formatMessage(memberDetailMessages.memberPulseReviewSourceNote)
    : sourceQuestionnaireResponse
    ? formatMessage(
        memberDetailMessages.memberDocumentsExtractionSourceQuestionnaireResponse,
      )
    : sourceDocument?.type?.text ||
      sourceDocument?.description ||
      sourceDocument?.content?.[0]?.attachment?.title ||
      formatMessage(
        memberDetailMessages.memberDocumentsExtractionSourceDocument,
      );

  const sourceLoadingPlaceholder = (
    <Box
      className={classes.sourcePanePlaceholder}
      data-testid={
        isSourceLoading
          ? 'extracted-resource-review-source-loading'
          : 'extracted-resource-review-source-unknown'
      }
    >
      {isSourceLoading ? (
        <PulseLoader
          width={96}
          label={formatMessage(
            memberDetailMessages.memberPulseReviewSourceLoading,
          )}
        />
      ) : (
        <Text size="subbody">
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceUnknownSourceDocument,
          )}
        </Text>
      )}
    </Box>
  );

  let sourcePaneBody: React.ReactNode;
  if (sourceAttachment) {
    sourcePaneBody = (
      <Box className={classes.sourcePaneBody}>
        <DocumentReferenceContent
          attachment={sourceAttachment}
          reference={sourceDocument ?? undefined}
          disableDeepLink
          disablePdfNav
          loader={
            <PulseLoader
              width={96}
              label={formatMessage(
                memberDetailMessages.memberPulseReviewSourceLoading,
              )}
            />
          }
        />
      </Box>
    );
  } else if (sourceQuestionnaireResponse?.id) {
    sourcePaneBody = (
      <Box
        className={classes.sourcePaneBody}
        data-testid="extracted-resource-review-source-questionnaire-response"
      >
        <FocusedSurvey
          responseId={sourceQuestionnaireResponse.id}
          onSurveyCompletion={() => undefined}
        />
      </Box>
    );
  } else if (sourceCommunication) {
    sourcePaneBody = (
      <Box
        className={classes.notePaneBody}
        data-testid="extracted-resource-review-source-note"
      >
        {noteContent ? (
          <MarkdownViewer markdown={noteContent} />
        ) : (
          <Text size="subbody">
            {formatMessage(
              memberDetailMessages.memberPulseReviewSourceNoteEmpty,
            )}
          </Text>
        )}
      </Box>
    );
  } else {
    sourcePaneBody = sourceLoadingPlaceholder;
  }

  const modalTitle =
    siblings.length > 1
      ? formatMessage(memberDetailMessages.memberPulseReviewQueueTitle, {
          count: siblings.length,
        })
      : selected.display;

  return (
    <Modal
      isOpen
      isFullScreen
      onDismiss={onDismiss}
      title={modalTitle}
      size={1}
      data-testid="extracted-resource-review-modal"
    >
      <Box className={classes.sideBySide}>
        <Box
          className={classes.sourcePane}
          data-testid="extracted-resource-review-source-pane"
        >
          <Box className={classes.sourcePaneHeader}>
            <Text size="subbody">{sourceTitle}</Text>
            {sourceCommunication && noteDate ? (
              <Text
                size="caption"
                className={classes.noteHeaderDate}
                data-testid="extracted-resource-review-source-note-date"
              >
                {formatReviewDate(noteDate)}
              </Text>
            ) : null}
          </Box>
          {sourcePaneBody}
        </Box>
        <Box className={classes.extractionPane}>
          {siblings.length > 1 ? (
            <Text size="caption" className={classes.queueHeader}>
              {formatMessage(
                memberDetailMessages.memberPulseReviewQueueSubtitle,
                {
                  count: siblings.length,
                },
              )}
            </Text>
          ) : null}
          {siblings.map(sibling => (
            <ExtractedResourceReviewCard
              key={`${sibling.resourceType}-${sibling.id}`}
              resourceType={sibling.resourceType}
              resource={sibling.resource}
              display={sibling.display}
              typeLabel={typeLabelFor(sibling.resourceType)}
              subjectId={subjectId}
              hideSourceDocumentLink={isSourceLoading || hasRenderedSource}
              reviewAnalyticsSurface={reviewAnalyticsSurface}
              onApproved={approval =>
                onResolveItem({
                  resourceType: sibling.resourceType,
                  id: approval.resourceId,
                  resolution: 'approved',
                })
              }
              onRejected={resourceId =>
                onResolveItem({
                  resourceType: sibling.resourceType,
                  id: resourceId,
                  resolution: 'rejected',
                })
              }
            />
          ))}
        </Box>
      </Box>
    </Modal>
  );
};
